import { useId, useState, type ChangeEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { publicUrl } from '@/lib/env';
import { prepararFotoProducto } from '@/lib/image-file';
import type { ProductVariantPhotos } from '@/types/products';
import {
  agregarFotosDeOpcion,
  ejesConOpciones,
  ejesParaFotos,
  elegirEjeFotos,
  filaEsDelEje,
  filaFotosDeOpcion,
  filasFueraDelEje,
  quitarFilaDeOpcion,
  quitarFotoDeOpcion,
  type OrderVariantForm,
} from './products-payload';
import {
  PRODUCT_LIMITS,
  PRODUCT_PHOTO_FORMATS_LABEL,
  PRODUCT_PHOTO_MAX_BYTES,
  validatePhotoCount,
  validatePhotoFile,
} from './products-validation';
import { useUploadProductPhoto } from './use-products';

/**
 * Fotos por color (21-sep-2026).
 *
 * El problema que resuelve: unas zapatillas que vienen en 5 colores tienen 5 fotos distintas y, con la
 * lista plana de «Fotos del producto», el bot no sabía cuál era la del rojo.
 *
 * **Una fila por VALOR de la variante** (una por color), como en Shopify: la foto se cuelga del color y
 * vale para TODAS las tallas de ese color. Nada de una fila por combinación (color × talla): eso
 * multiplicaba las filas y no aportaba nada, porque el bot manda la misma foto sea la talla 40 o la 42.
 *
 * El eje se elige solo: manda el que se llama «Color» (o tono/acabado) y, si ninguno se llama así, el
 * primero que **no sea de talla**; cuando hay más de un eje usable aparece el desplegable **«Fotos por»**
 * para cambiarlo (por si un negocio quiere fotos por otra variante). **La talla nunca se ofrece**
 * (24-sep-2026): un producto que solo tiene tallas no pinta filas por talla, se explica. Las filas que
 * quedan fuera de ese eje (una combinación guardada por otra vía) **no se tocan**: se avisan y se
 * conservan al guardar.
 *
 * Lo que se guarda en `fotosVariantes` es `{ variantes: { Color: 'Rojo' }, fotos: [...] }`, que es
 * exactamente lo que el motor usa para mandar la foto del color que el cliente pide.
 *
 * La subida es la MISMA que la de las fotos generales (`POST .../products/fotos` con
 * `useUploadProductPhoto`, reescalando en el navegador con `prepararFotoProducto`) y la URL devuelta es
 * lo que se guarda: el botón de guardar del producto es el único que persiste el catálogo.
 */
export interface VariantPhotoFieldsProps {
  /** `datosParaOrden` del formulario: de ahí salen los ejes de variante y sus opciones. */
  datosParaOrden: OrderVariantForm[];
  /** Filas de `fotosVariantes` del formulario (una por valor, como las guarda el backend). */
  values: ProductVariantPhotos[];
  onChange: (values: ProductVariantPhotos[]) => void;
  disabled?: boolean;
  className?: string;
}

export function VariantPhotoFields({
  datosParaOrden,
  values,
  onChange,
  disabled = false,
  className,
}: VariantPhotoFieldsProps) {
  const { tenantId } = useActiveTenantAccess();
  const uploadPhoto = useUploadProductPhoto(tenantId);
  const fieldId = useId();

  /** Eje que se está editando; `null` = todavía no se cambió y se usa el sugerido. */
  const [ejeElegido, setEjeElegido] = useState<string | null>(null);
  /** Opción cuya subida está en curso (mientras dura, la sección entera queda bloqueada). */
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** El backend avisa con `publicUrl: false` que la foto no tiene URL alcanzable por el servidor. */
  const [avisoUrlRelativa, setAvisoUrlRelativa] = useState(false);
  /** Cambiar la `key` permite volver a elegir el MISMO archivo después de quitarlo. */
  const [fileInputKey, setFileInputKey] = useState(0);

  const ocupado = disabled || subiendo !== null;
  const maxMb = Math.round(PRODUCT_PHOTO_MAX_BYTES / 1024 / 1024);

  const ejes = ejesParaFotos(datosParaOrden);
  const sugerido = elegirEjeFotos(datosParaOrden);
  // El eje elegido se mantiene mientras siga existiendo: si el usuario renombra la variante arriba, se
  // vuelve al sugerido en vez de quedarse editando uno que ya no está.
  const eje =
    ejeElegido && ejes.some((candidato) => candidato.nombre === ejeElegido) ? ejeElegido : sugerido;
  const opciones = ejes.find((candidato) => candidato.nombre === eje)?.opciones ?? [];
  const fueraDelEje = eje ? filasFueraDelEje(values, eje) : values.length;

  // Los hooks van SIEMPRE antes de este corte (regla de React), aunque no haya nada que editar.
  if (opciones.length === 0) {
    // Dos casos distintos, con mensajes distintos: el producto solo tiene TALLAS (y la talla no cambia la
    // foto, así que no se pinta ninguna fila) o no tiene variantes con opciones.
    const soloTallas = ejesConOpciones(datosParaOrden).length > 0;

    return (
      <div className={`space-y-2 ${className ?? ''}`.trim()}>
        <p className="text-caption text-ink-muted">
          {soloTallas ? (
            <>
              Este producto solo tiene variantes de <em>talla</em> y una foto no cambia entre la 40 y la 42:
              el bot manda la misma. Las fotos por color se cargan cuando hay un eje con <em>color</em>: si
              querés una foto por color, agregá en <em>Datos para la orden</em> un eje llamado{' '}
              <em>Color</em> con sus opciones (ej. Rojo, Azul…). Mientras tanto se mandan las fotos generales
              de arriba.
            </>
          ) : (
            <>
              Para darle una foto a cada color, cargá primero las variantes en <em>Datos para la orden</em>{' '}
              con su nombre y sus opciones (ej. <em>Color</em>: Rojo, Azul…). Sin opciones no hay nada que
              elegir y el bot manda siempre las fotos generales de arriba.
            </>
          )}
        </p>

        {/* Filas guardadas que ya no se editan NI se mandan (ej. las que quedaron por talla cuando se
            ofrecían): se pueden borrar desde acá, que es el único sitio donde el negocio las vería. */}
        {values.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-caption text-ink-faint">
              {`Hay ${values.length} ${values.length === 1 ? 'grupo' : 'grupos'} de fotos guardados que ya no se usan.`}
            </p>
            <Button
              variant="secondary"
              size="sm"
              disabled={ocupado}
              onClick={() => {
                setError(null);
                onChange([]);
              }}
            >
              Borrarlos
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  async function handleFiles(opcion: string, event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    setFileInputKey((key) => key + 1);
    if (files.length === 0) return;

    const indice = filaFotosDeOpcion(values, eje, opcion);
    const actuales = indice >= 0 ? (values[indice]?.fotos ?? []) : [];

    const countError = validatePhotoCount(actuales.length, files.length);
    if (countError) {
      setError(countError);
      return;
    }

    setError(null);
    setSubiendo(opcion);

    const subidas: string[] = [];
    let fallo: string | null = null;

    for (const file of files) {
      const fileError = validatePhotoFile(file);
      if (fileError) {
        fallo = fileError;
        break;
      }

      try {
        // Igual que en las fotos generales: se REESCALA en el navegador (máx. 1600 px) antes de subir.
        const { base64, mimeType } = await prepararFotoProducto(file);
        const saved = await uploadPhoto.mutateAsync({
          imageBase64: base64,
          ...(mimeType ? { mimeType } : {}),
        });
        subidas.push(saved.url);
        if (!saved.publicUrl) setAvisoUrlRelativa(true);
      } catch (err) {
        fallo = getErrorMessage(err);
        break;
      }
    }

    setSubiendo(null);
    // Lo que sí se subió no se pierde: entra a la lista aunque una foto posterior haya fallado.
    if (subidas.length > 0) onChange(agregarFotosDeOpcion(values, eje, opcion, subidas));
    if (fallo) setError(fallo);
  }

  function remove(opcion: string, indiceFoto: number) {
    setError(null);
    onChange(quitarFotoDeOpcion(values, eje, opcion, indiceFoto));
  }

  function quitarTodas(opcion: string) {
    setError(null);
    onChange(quitarFilaDeOpcion(values, eje, opcion));
  }

  /** Borra las filas que no son de este eje (combinaciones de antes): el panel no las puede editar. */
  function quitarFueraDelEje() {
    setError(null);
    onChange(values.filter((fila) => filaEsDelEje(fila, eje)));
  }

  const conFotos = opciones.filter((opcion) => filaFotosDeOpcion(values, eje, opcion) >= 0);

  return (
    <div className={className}>
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {avisoUrlRelativa ? (
        <Alert tone="warning">
          Alguna foto quedó guardada, pero su URL no es pública (falta `PUBLIC_BASE_URL` en el backend):
          ni la visión ni Meta podrán descargarla. Configúrala y vuelve a subir la foto.
        </Alert>
      ) : null}

      {ejes.length > 1 ? (
        <Field
          label="Fotos por"
          htmlFor={`${fieldId}-eje`}
          hint="Cada foto se cuelga de un valor de esta variante (una por color) y sirve para todas sus tallas."
        >
          <Select
            id={`${fieldId}-eje`}
            value={eje}
            disabled={ocupado}
            onChange={(event) => setEjeElegido(event.target.value)}
          >
            {ejes.map((candidato) => (
              <option key={candidato.nombre} value={candidato.nombre}>
                {candidato.nombre}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      <div className="mt-3 space-y-2">
        {opciones.map((opcion) => {
          const indice = filaFotosDeOpcion(values, eje, opcion);
          const fotos = indice >= 0 ? (values[indice]?.fotos ?? []) : [];

          return (
            <div key={opcion} className="rounded-panel border border-line bg-surface-muted/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-body font-medium text-ink">{opcion}</span>
                <span className="text-micro text-ink-faint">
                  {fotos.length}/{PRODUCT_LIMITS.fotos} fotos
                </span>
              </div>

              {fotos.length > 0 ? (
                <ul className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {fotos.map((foto, indiceFoto) => (
                    <li
                      key={`${foto}-${indiceFoto}`}
                      className="space-y-1 rounded-control border border-line bg-surface p-1"
                    >
                      <img
                        src={publicUrl(foto)}
                        alt={`Foto ${indiceFoto + 1} de ${opcion}`}
                        loading="lazy"
                        className="h-16 w-full rounded-control bg-surface-muted object-cover"
                      />

                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={ocupado}
                        onClick={() => remove(opcion, indiceFoto)}
                      >
                        Quitar
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-caption text-ink-faint">
                  Sin foto propia: si el cliente pide {opcion.toLowerCase()}, el bot manda las generales.
                </p>
              )}

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="min-w-56 flex-1">
                  <Input
                    key={`${fileInputKey}-${opcion}`}
                    id={`${fieldId}-archivo-${opcion}`}
                    name={`fotosVariante-${opcion}`}
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={ocupado}
                    aria-label={`Subir fotos de ${opcion}`}
                    onChange={(event) => void handleFiles(opcion, event)}
                  />
                </span>

                <Button
                  variant="ghost"
                  size="sm"
                  disabled={ocupado || fotos.length === 0}
                  onClick={() => quitarTodas(opcion)}
                >
                  Quitar todas
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-caption text-ink-muted">
        {conFotos.length > 0
          ? `${conFotos.length} de ${opciones.length} valores de ${eje} con foto propia: el bot manda las del valor que el cliente pida (y una de cada uno si solo dice «fotos»).`
          : `Ningún valor de ${eje} tiene foto propia todavía: el bot manda siempre las fotos generales de arriba.`}
      </p>

      {fueraDelEje > 0 ? (
        <p className="mt-1 text-micro text-ink-faint">
          Hay {fueraDelEje === 1 ? 'un grupo' : `${fueraDelEje} grupos`} de fotos de otra combinación (de
          antes o de otra variante): se conservan tal cual, pero no se editan acá.{' '}
          <button
            type="button"
            className="text-brand-600 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
            disabled={ocupado}
            onClick={quitarFueraDelEje}
          >
            Borrarlos
          </button>
        </p>
      ) : null}

      {subiendo ? (
        <p className="mt-1 text-caption text-ink-muted" role="status">
          Subiendo las fotos…
        </p>
      ) : (
        <p className="mt-1 text-micro text-ink-faint">
          {PRODUCT_PHOTO_FORMATS_LABEL} de hasta {maxMb} MB. Se sube al elegirla y se guarda con el
          producto. Las fotos tienen que ser alcanzables por el servidor: la visión y Meta las descargan
          desde su URL.
        </p>
      )}
    </div>
  );
}